import { useAccount } from "#contexts/AccountContext"
import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { showToast } from "#lib/toast"
import { homeFinancialSDK } from "#sdk/sdk"
import type { CreatePartner, UpdatePartner } from "@home-finance-manager/shared/types/partners"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

export function usePartners() {
  const { currentAccount } = useAccount()
  return useQuery({
    queryKey: queryKeys.partners.lists(),
    queryFn: () => homeFinancialSDK.accounts.partners.getPartners(),
    enabled: !!currentAccount,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function usePartner(id: number) {
  const { currentAccount } = useAccount()
  return useQuery({
    queryKey: queryKeys.partners.detail(id),
    queryFn: () => homeFinancialSDK.accounts.partners.getPartner(id),
    enabled: !!id && !!currentAccount,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useCreatePartner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePartner) => homeFinancialSDK.accounts.partners.createPartner(data),
    onMutate: async (newPartner) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.partners.all })
      const previousPartners = queryClient.getQueryData(queryKeys.partners.lists())

      queryClient.setQueryData(queryKeys.partners.lists(), (old: any[] | undefined) => {
        if (!old) return old
        const optimisticPartner = {
          ...newPartner,
          id: Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as any
        return [...old, optimisticPartner]
      })

      return { previousPartners }
    },
    onError: (_, __, context) => {
      if (context?.previousPartners) {
        queryClient.setQueryData(queryKeys.partners.lists(), context.previousPartners)
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all })
      showToast.success(
        `Partner "${variables.firstName} ${variables.lastName}" created successfully`
      )
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

export function useUpdatePartner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePartner }) =>
      homeFinancialSDK.accounts.partners.updatePartner(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.partners.all })
      const previousPartners = queryClient.getQueryData(queryKeys.partners.lists())
      const previousPartner = queryClient.getQueryData(queryKeys.partners.detail(id))

      queryClient.setQueryData(queryKeys.partners.lists(), (old: any[] | undefined) => {
        if (!old) return old
        return old.map((partner) =>
          partner.id === id ? { ...partner, ...data, updatedAt: new Date().toISOString() } : partner
        )
      })

      queryClient.setQueryData(queryKeys.partners.detail(id), (old: any) => {
        if (!old) return old
        return { ...old, ...data, updatedAt: new Date().toISOString() }
      })

      return { previousPartners, previousPartner }
    },
    onError: (_, variables, context) => {
      if (context?.previousPartners) {
        queryClient.setQueryData(queryKeys.partners.lists(), context.previousPartners)
      }
      if (context?.previousPartner) {
        queryClient.setQueryData(queryKeys.partners.detail(variables.id), context.previousPartner)
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.detail(variables.id) })
      showToast.success("Partner updated successfully")
    },
  })
}

export function useDeletePartner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => homeFinancialSDK.accounts.partners.deletePartner(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.partners.all })
      const previousPartners = queryClient.getQueryData(queryKeys.partners.lists())

      queryClient.setQueryData(queryKeys.partners.lists(), (old: any[] | undefined) => {
        if (!old) return old
        return old.filter((partner) => partner.id !== id)
      })

      return { previousPartners }
    },
    onError: (_, __, context) => {
      if (context?.previousPartners) {
        queryClient.setQueryData(queryKeys.partners.lists(), context.previousPartners)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all })
      showToast.success("Partner deleted successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}
